import type { ExternalLookupType } from '@/typings';

import { FormFieldType } from '../typings';
import type { FormFieldWarning, SingleFormField } from '../typings';
import {
  FACILITY_FREE_TEXT_WARNING,
  holdsFacilityFreeText,
  isFacilityLookup,
} from './facilityFreeText';

/**
 * Both sides are zero-padded `yyyy-MM-dd`, so lexicographic order is calendar
 * order. Comparing strings keeps the check free of timezone conversion — see
 * `parseDateFromString` for the same reasoning applied to parsing.
 */
const isDateWithinWarning = (value: string, warning: FormFieldWarning): boolean =>
  warning.maxValue !== undefined && value <= warning.maxValue;

/** The first warning whose condition the field's current value meets; a facility lookup's is its free-text warning. */
export const findActiveFieldWarning = (
  field: SingleFormField,
  value: unknown,
  lookupOptions?: { lookupType?: ExternalLookupType },
): FormFieldWarning | undefined => {
  // Keyed on the lookup type at runtime, so every facility field warns without seed work.
  if (field.type === FormFieldType.Select && isFacilityLookup(lookupOptions)) {
    return holdsFacilityFreeText(value, field.registryName)
      ? FACILITY_FREE_TEXT_WARNING
      : undefined;
  }

  if (!field.warnings?.length) return undefined;
  if (typeof value !== 'string' || value === '') return undefined;

  // Only dates carry seeded warnings; the field's own type picks the comparison.
  if (field.type !== FormFieldType.Date) return undefined;

  return field.warnings.find(warning => isDateWithinWarning(value, warning));
};
